import { useEffect, useState } from "react";

import { postJSONWithCSRF } from "@/utils/swr";

import EnquiryErrorView from "./EnquiryErrorView";
import EnquiryIntroView from "./EnquiryIntroView";
import styles from "./EnquiryModal.module.css";
import EnquiryPersonalForm, {
  EnquiryPersonalFormData,
} from "./EnquiryPersonalForm";
import EnquirySuccessView from "./EnquirySuccessView";
import { EnquiryTravelFormData } from "./EnquiryTravelForm";

interface EnquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalState = "travel-details" | "personal-details" | "success" | "error";

interface EnquiryResponseData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
}

function useEnquiryModal(props: EnquiryModalProps) {
  const { isOpen, onClose } = props;
  const [modalState, setModalState] = useState<ModalState>("travel-details");
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [travelDetails, setTravelDetails] =
    useState<EnquiryTravelFormData | null>(null);
  const [personalDetails, setPersonalDetails] =
    useState<EnquiryPersonalFormData | null>(null);
  const [enquiryResponse, setEnquiryResponse] =
    useState<EnquiryResponseData | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden"; // Prevent scrolling when modal is open
    } else {
      setIsVisible(false);
      setTimeout(() => {
        document.body.style.overflow = ""; // Re-enable scrolling
      }, 200); // Match transition time
    }

    // Reset modal state when reopening
    if (isOpen) {
      setModalState("travel-details");
      setTravelDetails(null);
      setPersonalDetails(null);
      setEnquiryResponse(null);
    }
  }, [isOpen]);

  return {
    isVisible,
    modalState,
    setModalState,
    isSubmitting,
    setIsSubmitting,
    travelDetails,
    setTravelDetails,
    personalDetails,
    setPersonalDetails,
    enquiryResponse,
    setEnquiryResponse,
    onClose,
  };
}

function EnquiryModal(props: EnquiryModalProps) {
  const {
    isVisible,
    modalState,
    setModalState,
    isSubmitting,
    setIsSubmitting,
    travelDetails,
    setTravelDetails,
    personalDetails,
    setPersonalDetails,
    setEnquiryResponse,
    onClose,
  } = useEnquiryModal(props);

  const handleTravelDetailsContinue = (details: EnquiryTravelFormData) => {
    setTravelDetails(details);
    setModalState("personal-details");
  };

  const handlePersonalDetailsBack = () => {
    setModalState("travel-details");
  };

  const handlePersonalDetailsSubmit = async (
    details: EnquiryPersonalFormData,
  ) => {
    if (!travelDetails) return;

    setPersonalDetails(details);
    setIsSubmitting(true);

    try {
      // Get CSRF token from meta tag
      const metaToken = document.querySelector('meta[name="csrf-token"]');
      const csrfToken = metaToken ? metaToken.getAttribute("content") : "";

      const submitEnquiry = postJSONWithCSRF<{ data: EnquiryResponseData }>(
        "Failed to submit enquiry",
        {
          createBody: ({ arg }) => JSON.stringify(arg),
          requestOptions: {
            headers: {
              "X-CSRF-Token": csrfToken || "",
            },
          },
        },
      );

      const response = await submitEnquiry("/api/enquiries_new", {
        arg: {
          date: travelDetails.arrival_date,
          group_size: parseInt(travelDetails.group_size, 10),
          email: details.email,
          title: details.title,
          first_name: details.first_name,
          last_name: details.last_name,
          dialing_code: details.dialing_code,
          mobile: details.mobile,
          package_id: null, // Use null for general enquiries
          receive_offers: details.receive_offers,
          destination: travelDetails.destination,
          nights: parseInt(travelDetails.nights, 10),
          rounds: parseInt(travelDetails.rounds, 10),
          include_flight_options: travelDetails.include_flight_options,
          flight_options: travelDetails.flight_options,
          additional_information: details.additional_information,
          type: "general_enquiry",
        },
      });

      setEnquiryResponse(response.data);
      setModalState("success");
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalState("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleRetry = () => {
    setModalState("travel-details");
  };

  if (!props.isOpen && !isVisible) {
    return null;
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  };

  const renderContent = () => {
    switch (modalState) {
      case "travel-details":
        return (
          <EnquiryIntroView
            travelDetails={travelDetails}
            onContinue={handleTravelDetailsContinue}
          />
        );

      case "personal-details":
        return (
          <EnquiryPersonalForm
            onSubmit={handlePersonalDetailsSubmit}
            onBack={handlePersonalDetailsBack}
            initialData={personalDetails || undefined}
            isSubmitting={isSubmitting}
            summaryInfo={
              travelDetails
                ? {
                    destination: travelDetails.destination || "Golf Break",
                    date: formatDate(travelDetails.arrival_date),
                    nights: travelDetails.nights,
                    rounds: travelDetails.rounds,
                  }
                : undefined
            }
          />
        );

      case "success":
        return (
          <EnquirySuccessView
            travelDetails={travelDetails}
            personalDetails={personalDetails}
          />
        );

      case "error":
        return <EnquiryErrorView onRetry={handleRetry} />;
    }
  };

  return (
    <div
      className={`${styles.modal} ${isVisible ? styles.open : ""}`}
      onClick={handleClose}
      aria-modal="true"
      role="dialog"
    >
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close modal"
          type="button"
        >
          &times;
        </button>

        <div className={styles.modalBody}>{renderContent()}</div>
      </div>
    </div>
  );
}

export default EnquiryModal;
export { EnquiryModal, type EnquiryModalProps };
